import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Snowflake, Sparkles, Lock } from "lucide-react";

/**
 * StreakCalendar — Iter 69 / Phase 2 of gamification.
 *
 * A 7-day visual grid that shows the user where their daily-reading
 * streak stands. Cells are color-coded:
 *   • kept           → solid orange (with Flame on today's cell)
 *   • frozen         → cyan with Snowflake icon (a Streak Freeze saved it)
 *   • today_pending  → outlined orange — "you haven't kept today yet"
 *   • missed         → dim gray
 *
 * Below the grid we show:
 *   • Current streak count (big number, animated)
 *   • Longest streak (smaller, "Best" label)
 *   • Freeze inventory pill — Limitless users get 1/week auto-replenished
 *
 * Mounted on the dashboard so returning users see their streak status
 * the moment they land, without needing to open the hamburger.
 */
export default function StreakCalendar() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/streak/calendar", { params: { days: 7 } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) return null;
  if (!data) {
    // Lightweight skeleton — same height as the loaded state so the
    // dashboard doesn't shift when the data arrives.
    return (
      <section className="glass border border-white/10 rounded-2xl p-5 animate-pulse" data-testid="streak-calendar-loading">
        <div className="h-3 w-32 bg-white/10 rounded mb-4" />
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-white/[0.04]" />
          ))}
        </div>
      </section>
    );
  }

  const cells = data.cells || [];
  const cur = data.current || 0;
  const best = data.longest || 0;
  const freezes = data.freezes_available || 0;
  const isLim = !!data.is_limitless;
  // Newly-minted users have zero streak data — show a soft empty state
  // instead of a sad grid of grey cells.
  const isFresh = cur === 0 && cells.every((c) => c.status === "missed" || c.status === "today_pending");

  return (
    <section
      className="glass border border-white/10 rounded-2xl p-5"
      data-testid="streak-calendar"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="label-eyebrow flex items-center gap-2">
          <Flame size={12} className="text-orange-400" /> Daily streak
        </div>
        <div className="flex items-center gap-2">
          {/* Freeze pill — shown for everyone (locked state for free users
              so the feature is discoverable, not invisible). Clicking
              the locked pill opens the upgrade modal so the visible
              perk doubles as a free→paid conversion lever. */}
          <button
            type="button"
            onClick={() => {
              if (!isLim) {
                window.dispatchEvent(new Event("oak:open-upgrade"));
              }
            }}
            disabled={freezes > 0 || isLim}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-display font-semibold border transition ${
              freezes > 0
                ? "bg-cyan-400/12 text-cyan-200 border-cyan-400/40 cursor-default"
                : isLim
                ? "bg-white/5 text-white/45 border-white/15 cursor-default"
                : "bg-white/5 text-white/45 border-white/15 hover:text-white hover:border-white/35 hover:bg-white/10 cursor-pointer"
            }`}
            title={
              freezes > 0
                ? "Auto-saves your streak if you miss a day."
                : isLim
                ? "Next free Streak Freeze unlocks Monday."
                : "Streak Freeze is a Limitless perk — tap to upgrade."
            }
            data-testid="streak-freeze-pill"
          >
            {!isLim && freezes === 0 ? (
              <Lock size={9} />
            ) : (
              <Snowflake size={10} className={freezes > 0 ? "" : "opacity-50"} />
            )}
            {freezes > 0 ? `${freezes} freeze${freezes === 1 ? "" : "s"}` : isLim ? "Refills Mon" : "Limitless only"}
          </button>
        </div>
      </div>

      {/* 7-day grid */}
      <div className="grid grid-cols-7 gap-1.5" data-testid="streak-calendar-grid">
        {cells.map((c) => (
          <CalendarCell key={c.date} cell={c} />
        ))}
      </div>

      {/* Numbers row */}
      <div className="mt-4 flex items-end justify-between">
        <div data-testid="streak-current">
          <div className="font-display text-3xl tracking-tight tabular-nums">
            <span
              className={`bg-clip-text text-transparent bg-gradient-to-r ${
                cur > 0 ? "from-orange-300 to-amber-100" : "from-white/40 to-white/30"
              }`}
            >
              {cur}
            </span>
            <span className="text-white/45 text-sm font-mono-oak ml-1">day{cur === 1 ? "" : "s"}</span>
          </div>
          {isFresh ? (
            <div className="text-[11px] text-white/45 font-mono-oak mt-0.5">Read a Part or pass a quiz to start your streak.</div>
          ) : (
            <div className="text-[11px] text-white/45 font-mono-oak mt-0.5">
              Best: {best} day{best === 1 ? "" : "s"}
            </div>
          )}
        </div>
        {!isFresh && cur >= 3 && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-400/12 text-orange-200 border border-orange-400/40 text-[10px] font-display font-semibold">
            <Sparkles size={9} /> On fire
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Single calendar cell. Aspect-square so the grid scales cleanly on
 * mobile widths. We don't show the date number — too small to read at
 * the target size and the weekday letter is enough context.
 */
function CalendarCell({ cell }) {
  const s = cell.status;
  let cls = "border border-white/10 bg-white/[0.03] text-white/40";
  let inner = null;
  if (s === "kept") {
    cls = "bg-gradient-to-br from-orange-500/35 to-amber-400/25 border-orange-400/45 text-orange-100";
    inner = cell.is_today ? <Flame size={11} /> : null;
  } else if (s === "frozen") {
    cls = "bg-cyan-400/15 border-cyan-400/35 text-cyan-200";
    inner = <Snowflake size={11} />;
  } else if (s === "today_pending") {
    cls = "bg-white/[0.04] border-orange-400/35 text-orange-200/85 animate-pulse";
    inner = <Flame size={11} className="opacity-60" />;
  }
  return (
    <div
      className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 transition ${cls}`}
      title={`${cell.date} — ${s}`}
      data-testid={`streak-cell-${cell.date}`}
      data-status={s}
    >
      <span className="text-[9px] uppercase tracking-wider font-mono-oak opacity-65">
        {cell.weekday?.[0] || "?"}
      </span>
      {inner}
    </div>
  );
}
